/**
 * PATH: apps/quote/src/components/builder/panels/inspector/MaterialsTab.tsx
 * PURPOSE: Materials tab with full CRUD operations for the row inspector
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Package, Plus, Trash2, Loader2 } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import { quoteApi, type QuoteLineItem } from '@/lib/quoteApi'

interface MaterialsTabProps {
  quoteId: string
  sectionId: string
  lineItem: QuoteLineItem
  onMaterialsUpdated?: () => void
}

export default function MaterialsTab({
  quoteId,
  sectionId,
  lineItem,
  onMaterialsUpdated,
}: MaterialsTabProps) {
  const materials = lineItem.materials || []
  const queryClient = useQueryClient()

  const addMaterialMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      quoteApi.addMaterial(quoteId, sectionId, lineItem.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', quoteId] })
      onMaterialsUpdated?.()
    },
  })

  const deleteMaterialMutation = useMutation({
    mutationFn: async (materialId: string) =>
      quoteApi.deleteMaterial(quoteId, sectionId, lineItem.id, materialId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', quoteId] })
      onMaterialsUpdated?.()
    },
  })

  const handleAddMaterial = () => {
    const slotOrder = ['primer', 'basecoat', 'topcoat', 'additional']
    const usedSlots = new Set(materials.map((m) => m.slot))
    const nextSlot = slotOrder.find((s) => !usedSlots.has(s)) || 'additional'

    addMaterialMutation.mutate({
      slot: nextSlot,
      product_name: 'New Material',
      unit_cost: 0,
      coverage_per_unit: 1,
      wastage_percent: 0,
    })
  }

  const handleDeleteMaterial = (materialId: string) => {
    if (confirm('Remove this material?')) {
      deleteMaterialMutation.mutate(materialId)
    }
  }

  const isMutating = addMaterialMutation.isPending || deleteMaterialMutation.isPending

  return (
    <div className="p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Materials</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Up to 4 material components (primer, basecoat, topcoat, additional)
          </p>
        </div>
        <button
          onClick={handleAddMaterial}
          disabled={isMutating || materials.length >= 4}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {addMaterialMutation.isPending ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Plus className="w-3 h-3" />
          )}
          Add
        </button>
      </div>

      {materials.length === 0 ? (
        <div className="text-center py-6 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
          <Package className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-600 mb-1" />
          <p className="text-sm text-slate-500 dark:text-slate-400">No materials added</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
            Apply an application or add manually
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {materials.map((mat, idx) => (
            <div
              key={mat.id || idx}
              className="p-2.5 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400 uppercase font-medium">
                      {mat.slot || 'material'}
                    </span>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      {mat.product_name}
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-1 mt-1.5 text-xs">
                    <div>
                      <span className="text-slate-500">Unit:</span>
                      <span className="ml-1 text-slate-900 dark:text-white">
                        {formatCurrency(mat.unit_cost || 0)}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Cover:</span>
                      <span className="ml-1 text-slate-900 dark:text-white">
                        {mat.coverage_per_unit}m²
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">/m²:</span>
                      <span className="ml-1 text-slate-900 dark:text-white">
                        {formatCurrency(mat.cost_per_m2 || 0)}
                      </span>
                    </div>
                  </div>
                  {mat.wastage_percent > 0 && (
                    <div className="mt-1 text-xs text-slate-500">
                      Wastage: {(mat.wastage_percent * 100).toFixed(0)}%
                    </div>
                  )}
                </div>
                <button
                  onClick={() => mat.id && handleDeleteMaterial(mat.id)}
                  disabled={deleteMaterialMutation.isPending}
                  className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-500 disabled:opacity-50"
                >
                  {deleteMaterialMutation.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="p-2.5 bg-slate-100 dark:bg-slate-800 rounded-lg space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Material Cost:</span>
          <span className="font-medium text-slate-900 dark:text-white">
            {formatCurrency(lineItem.material_cost || 0)}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Chargeout:</span>
          <span className="font-medium text-emerald-600">
            {formatCurrency(lineItem.material_chargeout || 0)}
          </span>
        </div>
      </div>
    </div>
  )
}
